const isArray = require('lodash/isArray');
const isObject = require('lodash/isObject');
const set = require('lodash/set');
const has = require('lodash/has');

const join = require('./join');
const split = require('./split');
const { normaliseKey, isNumericKey, isSimpleKey } = require('./key-utils');

const SEPARATOR = '.';

/**
 * Convert an array of pathValue expressions to an object of nested keys and values
 * @param {Array<PathValue>} pathValueArray array of pathValue objects
 * @returns {Object} the resulting converted object
 * @example
 * toObject([
 *   { path: 'fx.pairs[0].bid', value: 1.01 },
 *   { path: 'fx.pairs[0].ask', value: 1.02 },
 *   { path: 'fx.pairs[1].bid', value: 1.03 },
 *   { path: 'fx.pairs[1].ask', value: 1.04 },
 *   { path: 'fx.pairs[2].bid', value: 1.05 },
 *   { path: 'fx.pairs[2].ask', value: 1.06 },
 *  ]);
 *  // =>
 *  // {
 *  //   fx: {
 *  //     pairs: [
 *  //       { bid: 1.01, ask: 1.02 },
 *  //       { bid: 1.03, ask: 1.04 },
 *  //       { bid: 1.05, ask: 1.06 },
 *  //     ],
 *  //   }
 *  // }
 */
function toObjectHash(pathValueArray = /* istanbul ignore next */ []) {
  const obj = {};
  pathValueArray.forEach(({ path, value }) => {
    const stringPath = isArray(path) ? join(path) : path;
    set(obj, stringPath, value);
  });
  return obj;
}

/**
 * Convert an object to an array of pathValue objects
 * @param {Object} json to object to convert
 * @param {boolean} [asStrings] `true` to return the paths as strings,
 * `false` as Falcor array syntax
 * @returns {Array<PathValue<string>>|Array<PathValue<Array>>} the converted array of pathValues
 * @example
 * toPathArray({
 *   fx: {
 *     pairs: [
 *       { bid: 1.01, ask: 1.02 },
 *       { bid: 1.03, ask: 1.04 },
 *       { bid: 1.05, ask: 1.06 },
 *     ],
 *   },
 *   usersById: {
 *     'first.last@db.com': {
 *        name: 'first last',
 *     },
 *   },
 * });
 * // =>
 * // [
 * //   { path: 'fx.pairs[0].bid', value: 1.01 },
 * //   { path: 'fx.pairs[0].ask', value: 1.02 },
 * //   { path: 'fx.pairs[1].bid', value: 1.03 },
 * //   { path: 'fx.pairs[1].ask', value: 1.04 },
 * //   { path: 'fx.pairs[2].bid', value: 1.05 },
 * //   { path: 'fx.pairs[2].ask', value: 1.06 },
 * //   { path: 'usersById["first.last@db.com"].name', value: 'first last' },
 * //  ]
 */
function toPathArray(json = /* istanbul ignore next */ {},
                     asStrings = /* istanbul ignore next */ true) {
  const pathArray = getPathsFromObject(json);
  if (asStrings) {
    return pathArray;
  }
  return pathArray.map(({ path, value }) => ({ path: split(path), value }));
}

function getPathsFromObject(obj, paths = /* istanbul ignore next */ [],
                            parentPath = /* istanbul ignore next */ '') {
  Object.keys(obj).forEach((key) => {
    let currentPath;
    const normalisedKey = normaliseKey(key);
    if (isNumericKey(key) || !isSimpleKey(key)) {
      currentPath = parentPath.slice(0, -1) + normalisedKey;
    } else {
      currentPath = parentPath + normalisedKey;
    }
    const val = obj[key];
    if (isObject(val) && !has(val, '$type')) {
      getPathsFromObject(val, paths, currentPath + SEPARATOR);
    } else {
      paths.push({ path: currentPath, value: val });
    }
  });
  return paths;
}

module.exports = {
  toObjectHash,
  toPathArray,
};
